import { useMemo, useState } from 'react';
import type { AiDifficulty, MatchSetup } from '../../content/factions';
import { FACTIONS } from '../../content/factions';
import type { FactionId, MapId, MatchMode, OneVsOneMapId, TeamId, TwoVsTwoMapId } from '../../content/types';
import {
  DECK_SIZE,
  lastUsedDeckId,
  loadDecks,
  markDeckUsed,
  sanitizeDeckCards,
} from '../../app/persistence';
import { MapPicker } from '../MapPicker';
import vsAiBg from '../assets/vsai-bg.png';
import { Select } from '../components/Select';

const FACTION_IDS = Object.keys(FACTIONS) as FactionId[];
const IMPLEMENTED_FACTIONS = FACTION_IDS.filter((id) => FACTIONS[id].implemented);
const TEST_DECK = '__test__';

function adjacentFaction(current: FactionId, direction: -1 | 1): FactionId {
  const index = Math.max(0, IMPLEMENTED_FACTIONS.indexOf(current));
  return IMPLEMENTED_FACTIONS[(index + direction + IMPLEMENTED_FACTIONS.length) % IMPLEMENTED_FACTIONS.length]!;
}

function FactionStepper({ value, onChange, compact = false }: {
  value: FactionId;
  onChange: (faction: FactionId) => void;
  compact?: boolean;
}) {
  const faction = FACTIONS[value];
  return (
    <div className={`factionstepper${compact ? ' compact' : ''}`} aria-label="Faction selector">
      <button type="button" className="choicearrow" onClick={() => onChange(adjacentFaction(value, -1))} aria-label="Previous faction">‹</button>
      <div className={`factionchoice fac-${value}`} aria-live="polite">
        <span className="banneremblem"><span className="bannermark">{faction.mark}</span></span>
        <span className="bannername">{faction.name}</span>
      </div>
      <button type="button" className="choicearrow" onClick={() => onChange(adjacentFaction(value, 1))} aria-label="Next faction">›</button>
    </div>
  );
}

function deckCards(faction: FactionId, deckId: string): string[] {
  if (deckId === TEST_DECK) return [...FACTIONS[faction].defaultDeck];
  const deck = loadDecks(faction).find((candidate) => candidate.id === deckId);
  return deck ? sanitizeDeckCards(faction, deck.cards) : [...FACTIONS[faction].defaultDeck];
}

interface AiSlotState {
  faction: FactionId;
  difficulty: AiDifficulty;
}

function AiSlot({ label, value, team, onChange }: {
  label: string;
  value: AiSlotState;
  team: TeamId;
  onChange: (next: AiSlotState) => void;
}) {
  return (
    <div className={`aislot team-${team}`}>
      <strong>{label}</strong>
      <FactionStepper compact value={value.faction} onChange={(faction) => onChange({ ...value, faction })} />
      <Select
        ariaLabel={`${label} difficulty`}
        value={value.difficulty}
        options={[
          { value: 'easy' as AiDifficulty, label: 'Easy', hint: 'single-prong, no counter-play' },
          { value: 'standard' as AiDifficulty, label: 'Standard', hint: 'stages attacks, retreats as a group' },
          { value: 'hard' as AiDifficulty, label: 'Hard', hint: 'harasses, counters composition' },
        ]}
        onChange={(difficulty) => onChange({ ...value, difficulty })}
      />
    </div>
  );
}

export function FactionSelect({ onStart, onBack }: {
  onStart: (setup: MatchSetup) => void;
  onBack: () => void;
}) {
  const [mode, setMode] = useState<MatchMode>('1v1');
  const [mapId, setMapId] = useState<MapId>('crownline-crossing');
  const [playerFaction, setPlayerFaction] = useState<FactionId>('iron-covenant');
  const [deckId, setDeckId] = useState(() => lastUsedDeckId('iron-covenant') ?? TEST_DECK);
  const [ally, setAlly] = useState<AiSlotState>({ faction: 'verdant-wilds', difficulty: 'standard' });
  const [enemyOne, setEnemyOne] = useState<AiSlotState>({ faction: 'ashen-host', difficulty: 'standard' });
  const [enemyTwo, setEnemyTwo] = useState<AiSlotState>({ faction: 'astral-dominion', difficulty: 'standard' });

  const savedDecks = useMemo(() => loadDecks(playerFaction), [playerFaction]);
  const chosenCards = deckCards(playerFaction, deckId);
  const deckOk = chosenCards.length > 0;

  const changeMode = (next: MatchMode) => {
    setMode(next);
    setMapId(next === '2v2' ? 'fourfold-crossing' : 'crownline-crossing');
  };
  const pickFaction = (faction: FactionId) => {
    setPlayerFaction(faction);
    setDeckId(lastUsedDeckId(faction) ?? TEST_DECK);
  };
  const player = { faction: playerFaction, deck: chosenCards, isAI: false, team: 0 as const };
  const ai = (slot: AiSlotState, team: TeamId) => ({
    faction: slot.faction,
    deck: [...FACTIONS[slot.faction].defaultDeck],
    isAI: true,
    aiDifficulty: slot.difficulty,
    team,
  });
  const start = () => {
    if (!deckOk) return;
    if (deckId !== TEST_DECK) markDeckUsed(playerFaction, deckId);
    const setup: MatchSetup = mode === '2v2'
      ? { mode: '2v2', mapId: mapId as TwoVsTwoMapId, players: [player, ai(ally, 0), ai(enemyOne, 1), ai(enemyTwo, 1)] }
      : { mode: '1v1', mapId: mapId as OneVsOneMapId, players: [player, ai(enemyOne, 1)] };
    onStart(setup);
  };

  return (
    <div className="menu mustermenu" style={{ background: `#0a0c12 url(${vsAiBg}) center / cover no-repeat` }}>
      <div className="mustershade screen-in" />
      <div className="musterheader compact">
        <div className="mustertitles">
          <h2 className="mustertitle">Muster for Battle</h2>
          <div className="mustersub">Choose your format, forces, and battlefield.</div>
        </div>
        <button className="musterclose" onClick={onBack} aria-label="Back">×</button>
      </div>

      <div className="musterbody setupcompact">
        <div className="modetabs" role="tablist" aria-label="Match format">
          <button className={mode === '1v1' ? 'selected' : ''} onClick={() => changeMode('1v1')}>1v1 vs AI</button>
          <button className={mode === '2v2' ? 'selected' : ''} onClick={() => changeMode('2v2')}>2v2 with AI Ally</button>
        </div>

        <h3 className="setupheading">Your Faction</h3>
        <FactionStepper value={playerFaction} onChange={pickFaction} />

        <div className="musterdeckrow">
          <label className="musterdeck">
            <span>Deck</span>
            <Select
              ariaLabel="Deck"
              value={deckId}
              options={[
                { value: TEST_DECK, label: 'Faction test deck', hint: `${FACTIONS[playerFaction].defaultDeck.length} cards` },
                ...savedDecks.map((deck) => ({
                  value: deck.id,
                  label: deck.name,
                  hint: `${sanitizeDeckCards(playerFaction, deck.cards).length}/${DECK_SIZE}`,
                })),
              ]}
              onChange={setDeckId}
            />
          </label>
        </div>

        <h3 className="setupheading">Combatants</h3>
        <div className="aislots">
          {mode === '2v2' && <AiSlot label="AI Ally · Team Blue" value={ally} team={0} onChange={setAlly} />}
          <AiSlot label="Enemy 1 · Team Gold" value={enemyOne} team={1} onChange={setEnemyOne} />
          {mode === '2v2' && <AiSlot label="Enemy 2 · Team Gold" value={enemyTwo} team={1} onChange={setEnemyTwo} />}
        </div>

        <h3 className="setupheading">Battlefield</h3>
        <MapPicker mode={mode} selected={mapId} onSelect={setMapId} />

      </div>

      <div className="musterfooter">
        <button className="musterbtn back" onClick={onBack}>← Back</button>
        <button className="musterbtn startmatch" onClick={start} disabled={!deckOk}>⚔ Start Match</button>
      </div>
    </div>
  );
}
